<template>
  <div class="auth-branding-tab">
    <div class="scroll-ctn">
      <v-btn class="phone-preview-btn" @click="expandPreview" icon>
        <v-icon color="primary">mdi-cellphone</v-icon>
      </v-btn>

      <div class="pa-5">
        <div class="label font-weight-bold">
          {{$t('auth_tab_branding')}}
        </div>
        <div class="font-weight-light pa-3 pl-0">
          {{$t('auth_tab_customize_ui')}}<br>
          {{$t('auth_tab_customize_note')}}
        </div>

        <div class="font-weight-bold mt-6">
          <span class="label">{{$t('auth_tab_logo_label')}}</span><br>
          <span class="font-weight-light pa-3 pl-0">{{$t('auth_tab_logo_description')}}</span>
        </div>
        <v-row flex class="mt-4 ml-0">
          <v-btn v-if="isPreviewImage" class="border" color="primary" outlined @click="uploadImageModal = !uploadImageModal">
            {{$t('auth_tab_select_image')}}
          </v-btn>
          <div class="imagePreview ml-2" v-if="getAuthAppBranding?.logo">
            <img :src="getAuthAppBranding?.logo" alt="brand logo">
            <v-btn class="remove" @click="removeModal = true" icon>
              <v-icon>$remove</v-icon>
            </v-btn>
          </div>
        </v-row>

        <div class="font-weight-bold mt-6">
          <span class="label">{{$t('auth_tab_background_color_label')}}</span><br>
          <span class="font-weight-light pa-3 pl-0">{{$t('auth_tab_background_color_description')}}</span>
        </div>
        <color-input class="pa-3 pl-0" :color.sync="background"></color-input>

        <div class="font-weight-bold mt-6">
          <span class="label">{{$t('auth_tab_primary_color_label')}}</span><br>
          <span class="font-weight-light pa-3 pl-0">{{$t('auth_tab_primary_color_description')}}</span>
        </div>
        <color-input class="pa-3 pl-0" :color.sync="primaryColor"></color-input>

        <div class="font-weight-bold mt-6">
          <span class="label">{{$t('auth_tab_secondary_color_label')}}</span><br>
          <span class="font-weight-light pa-3 pl-0">{{$t('auth_tab_secondary_color_description')}}</span>
        </div>
        <color-input class="pa-3 pl-0" :color.sync="secondaryColor"></color-input>

<!--   Hide custom fonts. Waiting for the VA & PA release with the fonts feature    -->
<!--        <div class="font-weight-bold mt-6">-->
<!--          <span class="label">{{$t('font')}}:</span><br>-->
<!--          <span class="font-weight-light pa-3 pl-0">{{$t('auth_tab_font_description')}}</span>-->
<!--        </div>-->
<!--        <div class="pa-3 pl-0">-->
<!--          <font-picker-->
<!--            :value="getAuthAppDefaultFont"-->
<!--            :language-fonts="getAuthAppLanguageFonts"-->
<!--            :available-languages="overrideAvailableLanguages"-->
<!--            @update:value="updateAuthAppDefaultFont"-->
<!--            @update:language-font="updateAuthAppLanguageFont"-->
<!--          />-->
<!--        </div>-->
      </div>

      <div class="pa-5" v-if="isAdmin">
        <div class="advanced-setup" @click="showAdvancedSetup = !showAdvancedSetup">
          {{$t('auth_tab_advanced_setup')}}
          <v-icon
            class="arrow-icon"
            large
          >
            {{ showAdvancedSetup ? 'mdi-menu-up' : 'mdi-menu-down' }}
          </v-icon>
        </div>

        <div class="mt-9" v-if="showAdvancedSetup">
          <div class="d-flex flex-column mb-5">
            <div class="label font-weight-bold mb-2">
              {{ $t('auth_tab_landing_page_key') }}
            </div>
            <div class="d-flex ">
              <v-text-field
                :value="getLPKey"
                disabled
                hide-details
                class="mr-2 max-w-200"
                dense
              ></v-text-field>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" icon @click="copyToClipboard(getLPKey)">
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('auth_tab_copy_key') }}</span>
              </v-tooltip>
            </div>
          </div>
          <div>
            <div class="label font-weight-bold">
              {{$t('auth_tab_force_default_language_label')}}
            </div>
            <p class="my-2">
              {{$t('auth_tab_force_default_language_description1')}}<br>
              {{$t('auth_tab_force_default_language_description2')}}
            </p>

            <v-col
              class="d-flex pl-0"
              cols="12"
              sm="2"
            >
              <v-select
                v-model="advancedSettings.forceDefaultLanguage"
                :items="[{ name: 'None', key: '' }, ...languages]"
                @change="updateAdvancedSettings()"
                item-text="name"
                item-value="key"
                :label="$t('auth_tab_device_language')"
                solo
              ></v-select>
            </v-col>
          </div>

          <div>
            <div class="label font-weight-bold">
              {{$t('auth_tab_torch_control_label')}}
            </div>
            <p class="my-2">
              {{$t('auth_tab_torch_control_description')}}<br>
              <span class="alert">{{$t('auth_tab_torch_control_alert')}}</span>
            </p>

            <v-col
              class="d-flex pl-0"
              cols="12"
              sm="2"
            >
              <v-select
                v-model="advancedSettings.torchControlInVideoAuth"
                :items="torchOptions"
                @change="updateAdvancedSettings()"
                item-text="name"
                item-value="value"
                solo
              ></v-select>
            </v-col>
          </div>
        </div>
      </div>
    </div>

    <v-dialog width="500" v-model="removeModal">
      <v-card>
        <v-card-title class="headline">{{$t('delete_confirmation')}}</v-card-title>

        <v-card-text>
          <div class="my-5">
            <div>{{$t('auth_tab_delete_confirmation_text')}}</div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="removeModal = false">{{$t('cancel')}}</v-btn>
          <v-btn text color="error" @click="remove">{{$t('remove')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog width="500" v-model="uploadImageModal">
      <v-card>
        <v-card-title class="headline">{{$t('auth_tab_select_image')}}</v-card-title>

        <v-card-text>
          <div class="my-5 d-flex justify-space-around align-center g">
            <image-cropper input-key="brand-logo" @image-upload="handleSTGallerySelected" :hide-tooltip="true" />
            <div v-if="isAllowed('gallery_view')"  @click="displaySTGalleryDialog = true" class="cursor-pointer d-flex flex-column justify-center align-center">
              <v-btn color="accent" icon x-large>
                <v-icon>mdi-image-outline</v-icon>
              </v-btn>
              <span>{{$t('translations_select_image')}}</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="uploadImageModal = false">{{$t('cancel')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <select-image-dialog
      :display-select-image-dialog="displaySTGalleryDialog"
      @update:displaySelectImageDialog="displaySTGalleryDialog = !displaySTGalleryDialog"
      @gallery-selected="handleSTGallerySelected"
      :app-tags="['ulbs-editor-company-logo']"
      :isAllowedEdit="isAllowed('gallery_edit')"
    ></select-image-dialog>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import ColorInput from '@/components/utils/ColorInput.vue'
import { SelectImageDialog } from '@scantrust/st-gallery'
import Languages from '@/utils/languages'
import { AUTH_APP_LANGUAGES } from '@/utils/authAppLanguages'
import ImageCropper from '@/components/utils/ImageCropper.vue'
// import FontPicker from '@/components/FontPicker'

export default {
  name: 'AuthAppBrandingTab',
  components: {
    ImageCropper,
    ColorInput,
    SelectImageDialog
    // FontPicker
  },
  data () {
    return {
      removeModal: false,
      uploadImageModal: false,
      displaySTGalleryDialog: false,
      imageSrc: '',
      showAdvancedSetup: false,
      torchOptions: [{ name: 'On', value: true }, { name: 'Off', value: false }],
      languages: Languages.list,
      advancedSettings: {
        forceDefaultLanguage: '',
        torchControlInVideoAuth: true
      }
    }
  },
  created () {
    if (!this.getAuthAppBranding) {
      this.updateAdvancedSettings()
    } else {
      this.advancedSettings = {
        forceDefaultLanguage: this.getAuthAppBranding.forceDefaultLanguage,
        torchControlInVideoAuth: this.getAuthAppBranding.torchControlInVideoAuth
      }
    }
  },
  computed: {
    ...mapGetters([
      'getAuthAppBranding',
      'getAuthAppDefaultFont',
      'getAuthAppLanguageFonts',
      'defaultLanguage',
      'isAdmin',
      'isAllowed',
      'getLPKey'
    ]),

    overrideAvailableLanguages () {
      return AUTH_APP_LANGUAGES.filter(k => k !== this.defaultLanguage)
    },

    isPreviewImage: {
      get () {
        return !this.getAuthAppBranding?.logo
      }
    },
    background: {
      get () {
        return this.getAuthAppBranding?.background ? this.getAuthAppBranding?.background : '#FFFFFF'
      },
      set (newVal) {
        if (typeof newVal === 'string') {
          this.updateAuthAppBackgroundColor(newVal)
        }
      }
    },
    primaryColor: {
      get () {
        return this.getAuthAppBranding?.primary ? this.getAuthAppBranding?.primary : '#222222'
      },
      set (newVal) {
        if (typeof newVal === 'string') {
          this.updateAuthAppPrimaryColor(newVal)
        }
      }
    },
    secondaryColor: {
      get () {
        return this.getAuthAppBranding?.secondary ? this.getAuthAppBranding?.secondary : '#15BBF0'
      },
      set (newVal) {
        if (typeof newVal === 'string') {
          this.updateAuthAppSecondaryColor(newVal)
        }
      }
    }
  },
  watch: {
    imageSrc (newUrl) {
      this.updateAuthAppLogo(newUrl)
    },
    advancedSettings (newVal) {
      this.updateAuthAppAdvancedSettings(newVal)
    }
  },
  methods: {
    ...mapMutations({
      updateAuthAppBackgroundColor: 'UPDATE_AUTH_APP_BACKGROUND_COLOR',
      updateAuthAppPrimaryColor: 'UPDATE_AUTH_APP_PRIMARY_COLOR',
      updateAuthAppSecondaryColor: 'UPDATE_AUTH_APP_SECONDARY_COLOR',
      updateAuthAppLogo: 'UPDATE_AUTH_APP_BRANDING_LOGO',
      updateAuthAppAdvancedSettings: 'UPDATE_AUTH_APP_ADVANCED_SETTINGS',
      updateAuthAppDefaultFont: 'UPDATE_AUTH_APP_DEFAULT_FONT',
      updateAuthAppLanguageFont: 'UPDATE_AUTH_APP_LANGUAGE_FONT',
      changePreviewMode: 'CHANGE_PREVIEW_MODE'
    }),
    handleSTGallerySelected (url) {
      this.uploadImageModal = false
      this.imageSrc = url
    },
    remove () {
      this.imageSrc = ''
      this.removeModal = false
      this.updateAuthAppLogo(this.imageSrc)
    },
    updateAdvancedSettings () {
      this.updateAuthAppAdvancedSettings(this.advancedSettings)
    },
    expandPreview () {
      this.changePreviewMode(true)
    },
    copyToClipboard (text) {
      navigator.clipboard.writeText(text)
        .then(() => {
          this.$store.dispatch('showSnackBar', { message: 'Landing page key copied to clipboard! 🚀🚀🚀', duration: 5000 })
        })
        .catch(err => {
          console.error('Failed to copy key: ', err)
          this.$store.dispatch('showSnackBar', { message: 'Failed to copy landing page key to clipboard!', duration: 5000 })
        })
    }
  }
}

</script>

<style scoped lang="sass">
@import '@/variables.sass'
.auth-branding-tab
  height: calc(100% - 52px)
  position: relative
  border-bottom: none
  overflow: scroll
  margin: 0 !important
  .phone-preview-btn
    position: absolute
    top: -45px
    right: 10px
    @include lg
      display: none

.alert
  color: #FA4C5B
  font-weight: 500
.label
  text-transform: uppercase
  color: $label-grey

.no-alpha
  ::v-deep .v-color-picker__controls
    .v-color-picker__preview
      .v-color-picker__sliders
        .v-color-picker__alpha
          display: none
  ::v-deep .v-color-picker__swatch
    width: 33%
    padding: 0 5px
    .v-color-picker__color
      width: 100%

.scroll-ctn
  overflow: auto

.border
  border-color: rgba(0, 0, 0, 0.12)
.imagePreview
  position: relative
  max-width: 200px
  img
    width: 100%
  .remove
    position: absolute
    top: -15px
    right: -15px
    background: #fff

.advanced-setup
  color: #979797
  font-size: 16px
  cursor: pointer
  display: flex
  align-items: center
  &:after
    content: ''
    flex: 1
    height: 1px
    margin-left: 12px
    background-color: #F3F3F3
  .arrow-icon
    color: #5D5D5D
</style>
